import { useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  TextField,
  Button,
  Link,
  Paper,
  PaperProps,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { useTheme } from '@mui/material'

import { Alert } from '../Alert'

export type LoginFormErrors = {
  accessDenied?: boolean
}
export interface LoginFormProps extends Omit<PaperProps, 'children'> {
  handleLogin: (username: string, password: string) => Promise<boolean | void>
  errors: LoginFormErrors
}
export const LoginForm: React.FC<LoginFormProps> = ({
  handleLogin,
  errors,
  elevation = 1,
  ...props
}) => {
  const theme = useTheme()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [inSuspense, setInSuspense] = useState(false)

  const CURRICULUM_URL =
    process.env.REACT_APP_CURRICULUM_URL || 'https://hiv.uw.edu'

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setInSuspense(true)
    await handleLogin(username, password)
    setInSuspense(false)
  }

  return (
    <Paper
      {...props}
      sx={{
        padding: theme.spacing(2),
        textAlign: 'left',
        color: theme.palette.text.secondary,
        height: '100%',
        flexGrow: 1,
        ...props.sx,
      }}
      elevation={elevation}
    >
      <>
        {inSuspense ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            paddingTop={3}
          >
            <Typography variant="h6">Signing in...</Typography>
            <Box marginTop={4}>
              <CircularProgress size={100} />
            </Box>
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              marginBottom={3}
              flexDirection="column"
            >
              <Box marginBottom={1}>
                <Avatar
                  alt="Sign in"
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                  }}
                >
                  <LockIcon></LockIcon>
                </Avatar>
              </Box>
              <Typography variant="h6" color="textPrimary">
                Sign in to your account
              </Typography>
            </Box>
            <form onSubmit={handleSubmit}>
              <>
                {errors?.accessDenied && (
                  <Alert severity="error">Invalid username or password</Alert>
                )}
                <Box display="flex" flexDirection="column">
                  <TextField
                    label="Username"
                    variant="outlined"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    sx={{
                      '&:not(:last-child)': {
                        marginBottom: '20px',
                      },
                    }}
                  />
                  <TextField
                    label="Password"
                    variant="outlined"
                    name="password"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    sx={{
                      '&:not(:last-child)': {
                        marginBottom: '20px',
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{
                      '&:not(:last-child)': {
                        marginBottom: '20px',
                      },
                      marginTop: '10px',
                    }}
                  >
                    Sign In
                  </Button>
                  <Link
                    variant="body2"
                    style={{ cursor: 'pointer' }}
                    href={`${CURRICULUM_URL}/page/account/retrieval`}
                  >
                    Forgot your password?
                  </Link>
                </Box>
              </>
            </form>
          </>
        )}
      </>
    </Paper>
  )
}
export default LoginForm
